<template>
	<Application :loading="loading || productKindsLoading"
		:style="{ 'padding-left': isSideBarFixed ? '332px' : '32px', 'padding-right': '32px' }"
	>
		<template #navbar>
			<v-btn id="btnBack" elevation="0" style="background-color: transparent; min-width: 40px;" @click="goback()">
				<v-icon>mdi-chevron-left</v-icon>
			</v-btn>
			<span style="font-size: 17px; font-weight: 600; line-height: 22px;">{{ contractTemplateTitle }}</span>
		</template>
		<SideBar class="dcm-sidebar"
			:actions="sidebarActions"
			:navigation="sections"
			:preselectedActiveLink="'contracts'"
			:translations="translationStatuses"
			@action="handleAction($event)"
			@edit:translations="$refs.translationWizardDialog?.open"
			displayEditTranslations
		/>
		
		<!-- TODO: this should be a <Section> -->
		<div style="margin-top: 76px;" v-if="contractTemplate">
			<Disclosure :title="$t('text.contractInformation')" :error="!!childErrorMessage" expanded
				:message="childErrorMessage" ref="SECTION_contractInformation"
				:lock="hasContracts"
				:message-info="contractTemplate.metadata?.createdWithAi ? $t('text.contractTemplateCreatedWithAiInfoText') : undefined"
				class="contract-information-disclosure"
			>
				<FieldSet class="contract-information-block">
					<Field typeName="ContractTemplate" fieldName="name" v-model="contractTemplate.name" dataCy="name" :infoText="$t('text.contractTemplateNameInfoText')" :disabled="hasContracts" />
					<!-- MYS-7313: printed on the confirmation stamp of new signatures; stays editable with existing contracts because each signed contract keeps its own snapshot -->
					<Field class="stays-editable-when-locked" typeName="ContractTemplate" fieldName="officialName" v-model="contractTemplate.officialName" dataCy="officialName" :infoText="$t('text.contractTemplateOfficialNameInfoText')" />
					<Field typeName="ContractTemplate" fieldName="startDate" v-model="contractTemplate.startDate" dataCy="startDate" :disabled="hasContracts" />
					<Field typeName="ContractTemplate" fieldName="dueDays" v-model="contractTemplate.dueDays" dataCy="dueDays" :infoText="$t('text.contractTemplateDueDaysInfoText')" :disabled="hasContracts" />
					<Field typeName="ContractTemplate" fieldName="header" v-model="contractTemplate.header" dataCy="header" :infoText="$t('text.contractTemplateHeaderInfoText')" :disabled="hasContracts" />

					<FieldSet :label="$t('text.legalInformation')" :left-border="false" class="legal-information">
						<div class="legal-information-fields">
							<Field typeName="ContractTemplate" fieldName="legalName" v-model="contractTemplate.legalName" dataCy="legalName" :placeholder="$t('text.legalName')" style="flex: 1;" :disabled="hasContracts" />
							<Field typeName="ContractTemplate" fieldName="legalAddress" v-model="contractTemplate.legalAddress" dataCy="legalAddress" :placeholder="$t('text.legalAddressExample')" style="flex: 1;" :disabled="hasContracts" />
							<Field typeName="ContractTemplate" fieldName="clientAbbreviation" v-model="contractTemplate.clientAbbreviation" dataCy="clientAbbreviation" :placeholder="$t('text.clientAbbreviation')" style="flex: 1;" :disabled="hasContracts" />
						</div>
						
						<div class="contract-preview">
							<h3>{{ $t('text.contractHeaderPreview') }}</h3>
							<div class="contract-header-preview">
								<div class="contract-layout">
									<div class="contract-header-text" v-if="contractTemplate.header">
										<span v-html="contractTemplate.header[this.$store.state.selectedLocale] || $t('text.header')"></span>
									</div>
									<div class="contract-legal-info">
										<span class="contract-between-text">{{ $t('text.between') }}</span>
										<span class="contract-legal-field"><strong>{{ contractTemplate.legalName || $t('text.legalName') }}</strong></span>
										<span class="contract-comma">,</span>
										<span class="contract-legal-field"><strong>{{ contractTemplate.legalAddress || $t('text.legalAddress') }}</strong></span>
										<span class="contract-known-as">{{ $t('text.referredToAs') }}</span>
										<span class="contract-legal-field"><strong>{{ contractTemplate.clientAbbreviation || $t('text.clientAbbreviation') }}</strong></span>
									</div>
									<div class="contract-legal-info">
										<span class="contract-and">{{ $t('text.and') }}</span>
										<span class="contract-contractor-type">{{ isTourOperatorContract ? $t('text.tourOperator') : $t('text.serviceProvider') }}</span>
										<span class="contract-legal-field"><strong>{{ $t('text.contractorName') }}</strong> <i class="placeholder-hint">({{ $t('text.automaticallyGenerated') }})</i></span>
										<span class="contract-comma">,</span>
										<span class="contract-legal-field"><strong>{{ $t('text.contractorAddress') }}</strong> <i class="placeholder-hint">({{ $t('text.automaticallyGenerated') }})</i></span>
										<span class="contract-known-as">{{ $t('text.referredToAs') }}</span>
										<span class="contract-legal-field"><strong>{{ isTourOperatorContract ? $t('text.tourOperatorAbbreviation') : $t('text.contractorAbbreviation') }}</strong></span>
									</div>
								</div>
							</div>
						</div>
					</FieldSet>

					<Field typeName="ContractTemplate" fieldName="text" v-model="contractTemplate.text" dataCy="text" :infoText="$t('text.contractTemplateTextInfoText')" :disabled="hasContracts" />
				</FieldSet>
				<FieldSet v-if="!isTourOperatorContract" :label="$t('text.miscellaneous')" class="miscellaneous">
					<Field typeName="ContractTemplate" fieldName="needsSignature" :model-value="doesNotNeedSignature" isOnTheRightSide @update:model-value="(n) => doesNotNeedSignature = n" :infoText="$t('text.contractTemplateNeedsSignatureInfoText')" dataCy="needsSignature" :disabled="hasContracts" />
				</FieldSet>
				<FieldSet v-if="isTourOperatorContract" :label="$t('text.tourOperatorContractSpecificSettings')" class="to-specific-settings">
					<div class="tourOperatorContractHelp">
						<v-icon>mdi-lifebuoy</v-icon>
						<v-label style="margin-left: 8px;">{{ $t('text.tourOperatorContractHelpNeededLabel') }}</v-label>
						<p v-html="$t('text.tourOperatorContractHelpNeededInfoText')" />
					</div>
					<PaymentLevelsField v-model="contractTemplate.paymentLevels" :disabled="hasContracts" />
					<Field 
						typeName="ContractTemplate" 
						fieldName="serviceFee" 
						v-model="contractTemplate.serviceFee"
						placeholder="0.00"
						width="100px"
						class="serviceFeeField"
						:info-text="$t('text.tourOperatorContractServiceFeeInfoText')"
					/>
				</FieldSet>
				<!-- <FieldSet :label="$t('text.eCommerceCategory')"
				:info-text="$t('text.contractTemplateProductKindsInfoText')" :required="true">
				<Field v-model="contractTemplate.productKinds" typeName=" ContractTemplate" fieldName="productKinds"
					dataCy="productKinds">
					<ContractKindSelector v-model="contractTemplate.productKinds.de" :productKinds="productKinds" />
				</Field>
			</FieldSet> -->
			</Disclosure>
			<Disclosure v-if="isTourOperatorContract" :title="$t('text.tourOperatorContractDefaultDisclosure')" expanded>
				<Field
					class="tourOperatorContractDefaultField"
					typeName="ContractTemplate" 
					fieldName="default"
					:autoModel="false"
					:title="$t('text.tourOperatorContractDefaultTitle')"
					:infoText="$t('text.tourOperatorContractDefaultInfoText')"
					v-model="pendingDefaultValue"
				/>
			</Disclosure>
			<IfFeatureFlag flag="FEATURE_CONTRACT_TAGS">
				<Disclosure v-if="featureEnabled(Features.CONTRACT_TAGS)" :title="$t('text.contractTemplateTags')" expanded>
					<Field
						typeName="ContractTemplate"
						fieldName="tags"
						:title="$t('text.contractTemplateTags')"
						:infoText="$t('text.contractTemplateTagsInfoText')"
						:options="$store.state.loggedInUser?.fields.canManageTags.de"
						v-model="filteredContractTemplateTags"
					/>
				</Disclosure>
			</IfFeatureFlag>
		</div>
		<IfFeatureFlag flag="FEATURE_CONTRACTOR_CHANGES_WIZARD">
		<EntityHistory
			v-if="contractTemplate"
			:entity-type="EntityType.CONTRACT_TEMPLATE"
			:entity-id="contractTemplate.id"
			:executive="contractExecutive"
			changes-method-name="getContractTemplateChangesSince"
			recent-changes-method-name="getContractTemplateRecentChanges"
			page-visit-method-name="logContractTemplatePageVisit"
		/>
		</IfFeatureFlag>
		<IfFeatureFlag flag="FEATURE_TRANSLATION_WIZARD">
			<TranslationWizardDialog ref="translationWizardDialog" :fields="translatableFields" mode="metadata" />
		</IfFeatureFlag>
		<Dialog ref="changeDefaultTemplateDialog"
			data-cy="changeDefaultTemplateDialog"
			:confirmLabel="$t('text.tourOperatorContractDefaultChangeDialogConfirm')"
			:cancelLabel="$t('text.cancel')"
			:confirmHandler="onChangeDefaultSwitchDialogConfirm"
			:closeHandler="onChangeDefaultSwitchDialogCancel"
			:title="$t('text.tourOperatorContractDefaultChangeDialogTitle')"
			height="fit-content"
			width="540px"
		>
			<template #content>
				<div class="assign-dialog__confirm">
					<div class="assign-dialog__confirm__icon"><v-icon :color="'#00AEEF'" :size="'96px'">mdi-gavel</v-icon></div>
					<div>
						<v-label>{{ $t('text.tourOperatorContractDefaultChangeDialogTitle') }}</v-label>
						<p>{{ $t('text.tourOperatorContractDefaultChangeDialogText', { contractTemplate: defaultContractTemplateNameI18n }) }}</p>
					</div>
				</div>
			</template>
		</Dialog>
	</Application>
</template>

<script lang="ts">
import Common from '../../../mixins/Common.vue'
import SideBar from '../../../components/common/SideBar.vue'
import Application from '../Application.vue'
import ContractExecutive from '../../../../../api2/src/modules/contract/ContractExecutive'
import Disclosure from '../../../components/common/Disclosure.vue'
import Field from '../../../components/fields/Field.vue'
import ChildErrorReceiver from '../packageDesigner/ChildErrorReceiver.vue'
import FieldSet from '../packageDesigner/FieldSet.vue'
import ContractKindSelector from '../../../components/contract/ContractKindSelector.vue'
import { Translation, type ContractTemplate } from '../../../../../api2/src/modules/entities'
import Loading from '../../../mixins/Loading.vue'
import Toast from '../../../mixins/Toast.vue'
import EntityHistory from '../../../components/shared/EntityHistory.vue'
import { EntityType } from '../../../../../api2/src/modules/audit/audit.types'
import IfFeatureFlag from '../../../components/ifFeatureFlag/IfFeatureFlag.vue'
import TranslationWizardDialog from '../../../components/TranslationWizardDialog.vue'
import AiContext from '../../../mixins/AiContext.vue'
import PaymentLevelsField from '../../../components/contract/PaymentLevelsField.vue'
import { ContractSequenceType } from '../../../../../shared/types/account-types'
import Dialog from '../../../components/common/Dialog.vue'
import { Features } from '../../../../../shared/features'

export default {
	name: 'ContractTemplateDesigner',
	components: { SideBar, Application, Disclosure, Field, FieldSet, ContractKindSelector, EntityHistory, IfFeatureFlag, TranslationWizardDialog, Dialog, PaymentLevelsField },
	mixins: [ Common, ChildErrorReceiver, Loading, Toast, AiContext ],
	props: {
		id: String
	},
	data: () => ({
		contractTemplate: null as ContractTemplate | null,
		sections: [] as any[],
		productKinds: [] as any[],
		loading: false,
		productKindsLoading: false,
		activeLink: '',
		hasContracts: false,
		doesNotNeedSignature: false,
		ContractSequenceType, // Make the enum available in the template
		contractType: ContractSequenceType.SERVICE_PROVIDER as ContractSequenceType,
		EntityType, // Make the enum available in the template
		isDefault: false,
		defaultTourOperatorTemplate: null as ContractTemplate | null,
		pendingDefaultValue: false, // Store the pending value from the switch field until dialog is confirmed,
		Features, // Make the Features enum available in the template
	}),
	computed: {
		requiredTranslationLocaleCodes() {
			return this.getRequiredTranslationLocaleCodes()
		},
		contractTemplateTitle() {
			const nameByLocale = this.contractTemplate?.name || {}
			const selected = nameByLocale[this.selectedLocale]?.trim()
			const fallback = nameByLocale[this.defaultLocale.code]?.trim()
			return selected
				|| fallback
				|| Object.values(nameByLocale).find(value => value?.trim())
				|| ''
		},
		sidebarActions() {
			const actions = []
			actions.push({ icon: 'mdi-check', style: 'blue', dark: true, title: this.$t('text.saveChanges'), function: 'saveContractTemplate', params: 'save', datacy: "save", disabled: this.childErrorMessage })
			return actions
		},
		contractExecutive() {
			return new ContractExecutive(this)
		},
		translationStatuses() {
			return this.getTranslationStatus2(this.translatableFields, 'metadata', this.requiredTranslationLocaleCodes)
		},
		translatableFields() {
			if (!this.contractTemplate) return []
			return [
				{ typeName: 'ContractTemplate', fieldName: 'name', value: this.contractTemplate.name },
				this.contractTemplate.header ? { typeName: 'ContractTemplate', fieldName: 'header', value: this.contractTemplate.header } : undefined,
				{ typeName: 'ContractTemplate', fieldName: 'text', value: this.contractTemplate.text },
			].filter(f => !!f)
		},
		defaultContractTemplateNameI18n() {
			const nameByLocale = this.defaultTourOperatorTemplate?.name || {}
			const selected = nameByLocale[this.selectedLocale]?.trim()
			const fallback = nameByLocale[this.defaultLocale.code]?.trim()
			return selected
				|| fallback
				|| Object.values(nameByLocale).find(value => value?.trim())
				|| ''
		},
		filteredContractTemplateTags: {
			get(): string[] {
				return this.contractTemplate?.tags.filter(tag => this.$store.state.loggedInUser.fields.canManageTags.de.includes(tag)) || []
			},
			set(newTags: string[]) {
				if (this.contractTemplate) {
					const hiddenTags = this.contractTemplate?.tags.filter(tag => !this.$store.state.loggedInUser.fields.canManageTags.de.includes(tag)) || []
					this.contractTemplate.tags = [...hiddenTags, ...newTags]
				}
			},
		},
		isTourOperatorContract() {
			return this.contractType === ContractSequenceType.TOUR_OPERATOR
		},
	},
	watch: {
		pendingDefaultValue(newValue) {
			if (newValue === true) {
				if (this.defaultTourOperatorTemplate?.id && this.defaultTourOperatorTemplate.id !== this.contractTemplate?.id) {
					// If the current template is not the default tour operator template, show the dialog
					this.$refs['changeDefaultTemplateDialog'].show = true
				} else {
					// If the current template is the default tour operator template already, set isDefault
					this.isDefault = newValue
				}
			} else {
				this.isDefault = newValue
			}
		},
	},
	methods: {
		goback() {
			this.$store.state.contractsViewActiveLink = 'contracts'
			this.$router.push({
				path: '/contractManagement'
			})
		},
		async getContractTemplate() {
			try {
				this.loading = true
				let contractExecutive = new ContractExecutive(this)
				const result = await contractExecutive.getContractTemplateDetails(this.$store.state.selectedClient.sys.id, this.id)
				this.contractTemplate = result
				if (this.contractTemplate?.contractSequence?.type) {
					this.contractType = this.contractTemplate.contractSequence.type
				}

				if (this.isTourOperatorContract) {
					this.loadDefaultTourOperatorTemplate()
				}

				this.doesNotNeedSignature = !this.contractTemplate.needsSignature
				this.isDefault = this.contractTemplate.default
				this.pendingDefaultValue = this.isDefault
			}
			catch (error) {
				this.showErrorToast()
			}
			finally {
				this.loading = false
			}
		},
		async checkContractTemplateHasContracts() {
			const contractExecutive = new ContractExecutive(this)
			this.hasContracts = await contractExecutive.contractTemplateHasContracts(this.id)
		},
		async saveContractTemplate() {
			// cancel any AI context loading items before submit
			this.aiContext_cancel()

			// On save we write the actual local state to the contract template
			this.contractTemplate.default = this.isDefault

			await this.loadingOverlay(async () => {
				try {
					this.contractTemplate.needsSignature = !this.doesNotNeedSignature
					let contractExecutive = new ContractExecutive(this)
					await contractExecutive.upsertContractTemplate(this.$store.state.selectedClient.sys.id, this.contractTemplate, this.contractType)
					this.showSuccessToast()
					this.goback()
				}
				catch (error) {
					this.showErrorToast()
				}
			}, 'saveContractTemplate')
		},
		async getProductKinds() {
			this.productKindsLoading = true
			try {
				const response = await this.$httpGet(`/peak/attribute-sets/${this.$store.state.selectedClient.fields.installationId.de}`)
				this.productKinds = response?.attributeSets.map(x => x.attribute_set_name)
			}
			catch (error) {
				console.error('cannot load attribute sets', error)
			}
			this.productKindsLoading = false
		},
		async loadDefaultTourOperatorTemplate() {
			const contractExecutive = new ContractExecutive(this)
			this.defaultTourOperatorTemplate = await contractExecutive.getDefaultTourOperatorTemplate(this.$store.state.selectedClient.sys.id)
		},
		handleAction(event) {
			this[event.function](event.params)
		},
		setActive(key) {
			this.activeLink = key
		},
		setLocale(code) {
			this.serviceLocale = code
		},
		onChangeDefaultSwitchDialogConfirm() {
			// Apply the pending value from the switch field to local state
			this.isDefault = this.pendingDefaultValue

			this.$refs['changeDefaultTemplateDialog'].show = false
		},
		onChangeDefaultSwitchDialogCancel() {
			// Reset the pending value from the switch field and don't change anything
			this.pendingDefaultValue = this.contractTemplate?.default || false
			this.$refs['changeDefaultTemplateDialog'].show = false
		},
	},
	async mounted() {
		this.sections.push({ id: 'contracts', state: 'valid' })

		// Initialize serviceLocale to user's selectedLocale (English for an english-content client)
		if (this.contentEditingLocale()) {
			await this.$store.commit('setServiceLocale', this.contentEditingLocale())
		}

		// if we have an id, we are editing an existing contract template
		if (this.id) {
			this.getContractTemplate()
			this.checkContractTemplateHasContracts()
		}
		// if we don't have an id, we are creating a new contract template
		else {
			let contractExecutive = new ContractExecutive(this)
			const type = this.$route.query.type ? this.$route.query.type as ContractSequenceType : ContractSequenceType.SERVICE_PROVIDER
			this.contractTemplate = await contractExecutive.contractTemplateFactory(type)
			if (type === ContractSequenceType.TOUR_OPERATOR) {
				this.contractType = ContractSequenceType.TOUR_OPERATOR
				await this.loadDefaultTourOperatorTemplate()
			}
		}
	},
}
</script>
<style scoped lang="scss">
	.contract-information-disclosure > :deep(.v-expansion-panel) {
		padding: 0;

		> .v-expansion-panel-title {
			min-height: 84px;
			padding: 16px 64px;

			.normal-title {
				color: #000;
				font-size: 20px;
				font-weight: 600;
				line-height: 25px;
				letter-spacing: -0.196px;
			}
		}

		&.v-expansion-panel--active > .v-expansion-panel-title {
			border-bottom: 1px solid rgba(0, 0, 0, 0.1);
		}

		> .v-expansion-panel-text > .v-expansion-panel-text__wrapper {
			padding: 32px 64px;
		}

		@media (max-width: 767px) {
			> .v-expansion-panel-title,
			> .v-expansion-panel-text > .v-expansion-panel-text__wrapper {
				padding: 16px;
			}
		}

		.FieldSet.with-border {
			border-left: 2px solid #c1c1c1;
			padding-left: 12px;
		}

		.FieldSet.with-border.error {
			border-left-color: var(--col-error);
		}
	}

	.contract-information-block {
		display: flex;
		flex-direction: column;
		gap: 24px;
	}

	.miscellaneous {
		margin-top: 32px;

		> :deep(.FieldSet-label) {
			margin-bottom: 12px;
			color: #000;
			font-size: 17px;
			font-weight: 500;
			line-height: 22px;
			letter-spacing: -0.071px;
		}

		:deep(.Field label.title) {
			font-weight: 400;
		}

		:deep(.Field .infoText) {
			color: #8f8f8f;
			font-weight: 600;
		}
	}

	.legal-information {
		padding-left: 0;
	}

	.legal-information > :deep(.FieldSet-label) {
		margin-bottom: 4px;
		color: #000;
		font-size: 15px;
		font-weight: 500;
		line-height: 20px;
		letter-spacing: -0.037px;
	}

	.contract-information-block,
	.miscellaneous {
		:deep(.Field) {
			margin-bottom: 0;
		}

		:deep(.Field label.title) {
			margin-bottom: 4px;
			color: #000;
			font-size: 15px;
			line-height: 20px;
			letter-spacing: -0.037px;
		}

		:deep(.Field .infoText) {
			font-size: 12px;
			line-height: 16px;
			letter-spacing: 0.001px;
		}
	}

	.contract-information-block :deep(.Field label.title) {
		font-weight: 500;
	}

	.contract-preview {
		margin-top: 24px;
		margin-bottom: 0;
		border: 1px solid #e0e0e0;
		border-radius: 8px;
		padding: 16px;
		background-color: #f9f9f9;
	}

	.contract-preview h3 {
		margin-top: 0;
		margin-bottom: 16px;
		font-size: 16px;
		color: #555;
	}

	.contract-header-preview {
		border: 1px solid #ddd;
		border-radius: 4px;
		padding: 16px;
		background-color: white;

	}

	.contract-layout {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.contract-header-text {
		font-weight: bold;
		font-size: 16px;
	}

	.contract-legal-info {
		display: flex;
		flex-wrap: wrap;
		gap: 6px;
		align-items: center;
	}

	.contract-between-text,
	.contract-contractor-type {
		margin-right: 4px;
	}

	.contract-legal-field {
		font-size: 14px;
	}

	.contract-comma,
	.contract-known-as {
		font-size: 14px;
	}

	.legal-information-fields {
		display: flex;
		flex-direction: row;
		gap: 10px;
		flex: 1;
	}

	.tourOperatorContractDefaultField {
		opacity: 1;
		pointer-events: all;
		background-color: white;
	}

	.to-specific-settings {
		margin-top: 32px;
		> :deep(h2) {
			color: var(--primary-color-dark, #000);
			font-size: 17px;
			font-weight: 500;
			line-height: 22px;
			letter-spacing: -0.071px;
		}

		.tourOperatorContractHelp {
			margin: 24px 0;
			> label {
				color: #000 !important;
				opacity: 1;
				font-size: 15px !important;
				font-weight: 500;
				line-height: 20px;
				letter-spacing: -0.037px;
			}
			> p {
				margin-top: 4px;
				color: var(--primary-color-dark, #000);
				font-size: 15px;
				font-weight: 400;
				line-height: 20px;
				letter-spacing: -0.037px;
				:deep(a) {
					color: #00AEEF;
				}
				:deep(a:visited) {
					color: revert;
				}
			}
		}
		.serviceFeeField {
			margin-top: 24px;
		}
		.serviceFeeField :deep(.title-container .title) {
			color: var(--primary-color-dark, #000);
			font-size: 15px;
			font-weight: 500;
			line-height: 20px;
			letter-spacing: -0.037px;
			margin-bottom: 4px;
		}
		.serviceFeeField :deep(.info-text) {
			color: var(--primary-color-dark, #000);
			font-size: 12px;
			font-weight: 400;
			line-height: 16px;
			letter-spacing: 0.001px;
		}
	}


	.assign-dialog {
		&__confirm {
			display: flex;
			align-items: center;

			&__icon {
				margin-right: 48px;
			}
		}
	}
</style>
